import React from "react";
import { useNavigate } from "react-router-dom";
import * as momentTZ from "moment-timezone";
import dayjs, { Dayjs } from "dayjs";

import IFormikProps from "../../interfaces/others/IFormikProps";
import CustomSelectField from "../formik/CustomSelectField.tsx";
import CustomDateRange from "../formik/CustomDateRange.tsx";
import ChipInput from "material-ui-chip-input";
import {
  LocalizationProvider,
  MobileDatePicker,
  PickersDay,
  PickersDayProps,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  DemoContainer,
  DemoItem,
} from "@mui/x-date-pickers/internals/demo/DemoContainer";
import { Badge } from "@mui/material";
import { notification } from "../../utils/Notifications.tsx";
import { autocompletePartners } from "../../services/partner.service.ts";
import { findAllQuickAnswers } from "../../services/incident.service.ts";
import { useDispatch } from "react-redux";
import { setQuickAnswer } from "../../store/slices/incidenciaSlice.ts";
import BranchAutocomplete from "../common/BranchAutocomplete.tsx";

const IncidentsForm = ({
  formik,
  branches,
  disabled = false,
  setLoading,
  hideBranchAutocomplete = false,
  branchId,
  directBossEmployees,
}: IFormikProps & {
  branches: any[];
  disabled: boolean;
  setLoading: (status: boolean) => void;
  hideBranchAutocomplete?: boolean;
  branchId?: number;
  directBossEmployees?: any[];
}) => {
  const dispatch = useDispatch();

  const [showCalendar, setShowCalendar] = React.useState(false);
  const [currentMonth, setCurrentMonth] = React.useState(dayjs());
  const [chips, setChips] = React.useState<string[]>([]);
  const [date, setDate] = React.useState(dayjs());

  const [searchPartner, setSearchPartner] = React.useState<string>("");
  const [filteredPartners, setFilteredPartners] = React.useState<any[]>([]);
  const [showPartnerDropdown, setShowPartnerDropdown] = React.useState(false);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const partnerDropdownRef = React.useRef<HTMLDivElement>(null);
  const searchTimeoutRef = React.useRef<any>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        partnerDropdownRef.current &&
        !partnerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowPartnerDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadPartners = React.useCallback(
    async (searchTerm: string = "") => {
      if (directBossEmployees && Array.isArray(directBossEmployees)) {
        const term = searchTerm.trim().toLowerCase();
        const filtered = directBossEmployees
          .filter((p) => {
            const fullName = `${p.person?.firstName || ""} ${
              p.person?.lastName || ""
            } ${p.person?.secondLastName || ""}`.toLowerCase();
            return term ? fullName.includes(term) : true;
          })
          .slice(0, 10);
        setFilteredPartners(filtered);
        setSearchLoading(false);
        return;
      }

      try {
        setSearchLoading(true);

        const data = await autocompletePartners(searchTerm, branchId);
        setFilteredPartners(data || []);
      } catch (error) {
        setFilteredPartners([]);
      } finally {
        setSearchLoading(false);
      }
    },
    [branchId]
  );

  const handlePartnerSearch = async (searchTerm: string) => {
    setSearchPartner(searchTerm);
    setShowPartnerDropdown(true);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchTerm.trim()) {
      setFilteredPartners([]);
      (formik as any).setFieldValue("partnerId", null);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      loadPartners(searchTerm);
    }, 500);
  };

  const handlePartnerFocus = () => {
    setShowPartnerDropdown(true);

    if (!searchPartner && filteredPartners.length === 0) {
      loadPartners("");
    }
  };

  const handlePartnerSelect = (partner: any) => {
    setSearchPartner(`${partner.person.firstName} ${partner.person.lastName}`);
    setShowPartnerDropdown(false);
    (formik as any).setFieldValue("partnerId", partner.id);
  };

  const handleClearPartner = () => {
    setSearchPartner("");
    setFilteredPartners([]);
    (formik as any).setFieldValue("partnerId", null);
  };

  function ServerDay(
    props: PickersDayProps<Dayjs> & { highlightedDays?: number[] }
  ) {
    const {
      highlightedDays = [],
      day,
      outsideCurrentMonth,
      showDaysOutsideCurrentMonth,
      ...other
    } = props;

    const dayString = day.format("YYYY-MM-DD");

    if (outsideCurrentMonth) {
      return (
        <PickersDay
          {...other}
          outsideCurrentMonth={outsideCurrentMonth}
          day={day}
        />
      );
    }

    return (
      <Badge key={props.day.toString()} overlap="circular">
        <PickersDay
          {...other}
          outsideCurrentMonth={outsideCurrentMonth}
          day={day}
        />
      </Badge>
    );
  }

  const handleAddChip = (data: string[]) => {
    setChips(Array.from(new Set([...chips, ...data])));
  };

  const handleRemove = (index: number) => {
    setChips(chips.filter((_, i) => i !== index));
  };

  const fetchChipsAvailable = async () => {
    try {
      const quickAnswers = await findAllQuickAnswers();
      const quickAnswerTexts = quickAnswers.map((qa) => qa.text);
      setChips(quickAnswerTexts);
      dispatch(setQuickAnswer(quickAnswerTexts));
    } catch (error) {
      notification(
        "Error ⚠️",
        "Error al obtener las opciones rapidas",
        "danger"
      );
    }
  };

  React.useEffect(() => {
    fetchChipsAvailable();
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (
      directBossEmployees &&
      Array.isArray(directBossEmployees) &&
      directBossEmployees.length > 0 &&
      !searchPartner
    ) {
      const initial = directBossEmployees.slice(0, 10);
      setFilteredPartners(initial);
    }
  }, [directBossEmployees, searchPartner]);

  React.useEffect(() => {
    dispatch(setQuickAnswer(chips));
  }, [chips]);

  return (
    <div className="incidents-form-fields">
      {!hideBranchAutocomplete && (
        <BranchAutocomplete
          branches={branches}
          value={formik.values.branch}
          onChange={(branchId) => formik.setFieldValue("branch", branchId)}
          disabled={disabled}
          label="Sucursal"
        />
      )}

      <div className="row fs-13px col-12 mt-3 incidents-form-row">
        <label
          className="form-label col-form-label col-12 text-start"
          style={{
            fontWeight: "600",
            color: "#495057",
            fontSize: "13px",
          }}
        >
          Colaborador
        </label>
        <div className="col-12" ref={partnerDropdownRef} style={{ minWidth: 0 }}>
          <div className="position-relative w-100">
            <div className="input-group" style={{ maxWidth: "100%" }}>
              <div
                className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                style={{ zIndex: 10 }}
              >
                {searchLoading ? (
                  <i className="fa fa-spinner fa-spin" />
                ) : (
                  <i className="fa fa-search opacity-5" />
                )}
              </div>
              <input
                type="text"
                className="form-control px-35px"
                style={{
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  background:
                    "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
                  border: "1px solid #e0e0e0",
                  borderRadius: "14px",
                  fontSize: "13px",
                  transition: "all 0.3s ease",
                }}
                placeholder="Buscar colaborador..."
                value={searchPartner}
                onChange={(e) => handlePartnerSearch(e.target.value)}
                onFocus={handlePartnerFocus}
              />
              {formik.values.partnerId && (
                <div
                  className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 end-0"
                  style={{ zIndex: 10, cursor: "pointer" }}
                  onClick={handleClearPartner}
                  title="Limpiar selección"
                >
                  <i className="fa fa-times text-muted" />
                </div>
              )}
            </div>
            {showPartnerDropdown && filteredPartners.length > 0 && (
              <div
                className="position-absolute w-100 bg-white border rounded shadow-lg"
                style={{
                  zIndex: 1000,
                  maxHeight: "280px",
                  overflowY: "auto",
                  marginTop: "4px",
                  borderRadius: "14px",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                }}
              >
                {filteredPartners.map((partner) => (
                  <div
                    key={partner.id}
                    className="px-3 py-2"
                    style={{
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      borderBottom: "1px solid #f0f0f0",
                      fontSize: "13px",
                    }}
                    onClick={() => handlePartnerSelect(partner)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "linear-gradient(135deg, #fee 0%, #fdd 100%)";
                      e.currentTarget.style.transform = "translateX(4px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "white";
                      e.currentTarget.style.transform = "translateX(0)";
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <i
                        className="fa fa-user me-2 text-danger"
                        style={{ fontSize: "12px" }}
                      />
                      <span className="fw-500">
                        {partner.person.firstName} {partner.person.lastName}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row fs-13px col-12 mt-3 incidents-form-row">
        <label
          className="form-label col-form-label col-12 text-start"
          style={{
            fontWeight: "600",
            color: "#495057",
            fontSize: "13px",
          }}
        >
          Fecha
        </label>
        <div className="col-12">
          <button
            className="btn w-100"
            onClick={() => setShowCalendar(true)}
            style={{
              background:
                "linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #dc2626 100%)",
              color: "white",
              border: "none",
              borderRadius: "14px",
              padding: "10px 16px",
              fontSize: "13px",
              fontWeight: "500",
              transition: "all 0.3s ease",
              boxShadow:
                "0 4px 12px rgba(251, 191, 36, 0.4), 0 2px 4px rgba(220, 38, 38, 0.2)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 6px 16px rgba(251, 191, 36, 0.5), 0 4px 8px rgba(220, 38, 38, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 4px 12px rgba(251, 191, 36, 0.4), 0 2px 4px rgba(220, 38, 38, 0.2)";
            }}
          >
            {momentTZ
              .tz(formik.values.date, "YYYY-MM-DD", "America/Mexico_City")
              .format("DD [de] MMMM [del] YYYY")}
            &nbsp;&nbsp;
            <i className="fa-regular fa-calendar-check" />
          </button>
        </div>
      </div>

      {/* {(user.rol === "Administrador" || user.rol === "Administrador-") && (
        <div className="row fs-13px col-md-12 mt-3">
          <label
            className="form-label col-form-label col-md-4 text-end"
            style={{
              fontWeight: "600",
              color: "#495057",
              fontSize: "13px",
            }}
          >
            Ver incidencias resueltas
          </label>
          <div className={`col-md-4`}>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="showResolvedSwitch"
                checked={formik.values.showResolved || false}
                onChange={(e) => {
                  formik.setFieldValue("showResolved", e.target.checked);
                  formik.handleSubmit();
                }}
                style={{
                  width: "3rem",
                  height: "1.5rem",
                  cursor: "pointer",
                }}
              />
            </div>
          </div>
        </div>
      )} */}

      {/* <div className="row fs-13px col-md-12 mt-4">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-center mb-2">
            <i
              className="fa fa-bolt me-2"
              style={{ color: "#FF6B6B", fontSize: "14px" }}
            ></i>
            <label
              className="form-label mb-0"
              style={{
                fontWeight: "600",
                color: "#495057",
                fontSize: "13px",
              }}
            >
              Respuestas Rápidas
            </label>
          </div>
          <div
            style={{
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
            }}
          >
            <div style={{ width: "60%" }}>
              <ChipInput
                fullWidth
                placeholder="Escribe una respuesta rápida y presiona Enter"
                onChange={(c) => handleAddChip(c)}
                onDelete={(_, i) => handleRemove(i)}
                value={chips}
                chipRenderer={({ value, handleClick, handleDelete }, key) => {
                  const chipIndex = chips.findIndex((chip) => chip === value);
                  return (
                    <span
                      key={key}
                      onClick={handleClick}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "6px 12px",
                        margin: "4px",
                        borderRadius: "16px",
                        background: "#e9ecef",
                        color: "#495057",
                        fontSize: "12px",
                        fontWeight: "500",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                        border: "1px solid #dee2e6",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 6px rgba(0, 0, 0, 0.15)";
                        e.currentTarget.style.background = "#dee2e6";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.boxShadow =
                          "0 1px 3px rgba(0, 0, 0, 0.1)";
                        e.currentTarget.style.background = "#e9ecef";
                      }}
                    >
                      {value}
                      <i
                        className="fa fa-times ms-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (chipIndex !== -1) {
                            handleRemove(chipIndex);
                          }
                        }}
                        style={{ fontSize: "10px", opacity: 0.6 }}
                      />
                    </span>
                  );
                }}
              />
            </div>
          </div>
        </div>
      </div> */}

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"es"}>
          <DemoContainer
            components={["MobileDatePicker"]}
            sx={{
              pt: 0,
              mx: 1,
              cursor: "pointer",
              width: 0,
              height: 0,
            }}
          >
            <DemoItem>
              <MobileDatePicker
                open={showCalendar}
                sx={{ cursor: "pointer" }}
                className="form-control mb-5px fs-13px"
                value={dayjs(
                  formik.values.date || dayjs().format("YYYY-MM-DD")
                )}
                defaultValue={dayjs(date)}
                onClose={() => {
                  setShowCalendar(false);
                  formik.handleSubmit();
                }}
                onChange={(e: any) => {
                  console.log({ xd: dayjs(e).format("YYYY-MM-DD") });
                  formik.setFieldValue("date", dayjs(e).format("YYYY-MM-DD"));
                }}
                slots={{
                  day: ServerDay,
                }}
                onMonthChange={(e) => {
                  if (currentMonth.month() !== e.month()) {
                    setCurrentMonth(e);
                  }
                }}
              />
            </DemoItem>
          </DemoContainer>
        </LocalizationProvider>
      </div>
    </div>
  );
};

export default IncidentsForm;
